/**
 * Applications Service — lexon/shkruan te apiService (localStorage key: 'applications')
 * për të qenë konsistent me të dhënat e seeded.
 */
import apiService, { authFetch } from './apiServices';

function normalize(r) {
  if (!r) return r;
  // Suporton si formatin e ri (fullName) ashtu edhe formatin e vjetër (FirstName/LastName)
  return {
    ...r,
    id:        r.id || String(r.Id || ''),
    fullName:  r.fullName || [r.FirstName, r.LastName].filter(Boolean).join(' ') || '',
    email:     r.email || r.Email || '',
    phone:     r.phone || r.Phone || '',
    position:  r.position || r.Position || '',
    listName:  r.listName || r.ListName || '',
    source:    r.source || r.Source || '',
    status:    r.status || r.Status || 'applied',
    appliedAt: r.appliedAt || r.ApplyDate || r.createdAt || '',
    notes:     r.notes || r.Note || '',
    history:   Array.isArray(r.history) ? r.history : [],
    recruiterUserId: r.recruiterUserId ?? null,
    recruiterName:   r.recruiterName || '',
  };
}

/** `?a=1&b=2` nga një objekt, duke lënë jashtë bosh-et dhe 'all'. */
function qs(params = {}) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === 'all') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export default function applicationsService() {
  return {
    /**
     * Një FAQE kandidatësh, e filtruar nga serveri.
     *
     * Më parë kjo lexonte çdo rresht dhe faqja i filtronte në shfletues. Kthen
     * `{ rows, pagination, stats }`; numëruesit vijnë nga serveri mbi TË GJITHË
     * rreshtat e filtruar, jo mbi faqen — përndryshe «7 kandidatë» do të thoshte
     * «7 në këtë faqe».
     */
    async list(params = {}) {
      try {
        const res = await authFetch(`/api/applications${qs(params)}`).then((r) => r.json());
        if (!res?.success) return { rows: [], pagination: { page: 1, limit: 25, total: 0, totalPages: 1 }, stats: null };
        return {
          rows: (res.data.data || []).map(normalize),
          pagination: res.data.pagination,
          stats: res.data.stats,
        };
      } catch (_) {
        return { rows: [], pagination: { page: 1, limit: 25, total: 0, totalPages: 1 }, stats: null };
      }
    },

    /**
     * I gjithë grupi i filtruar — për eksport.
     *
     * Pa këtë, paginimi do ta zvogëlonte në heshtje eksportin te rreshtat e
     * dukshëm, dhe askush s'do ta vinte re derisa një raport të dilte i gabuar.
     */
    async exportAll(params = {}) {
      try {
        const res = await authFetch(`/api/applications/export${qs(params)}`).then((r) => r.json());
        return res?.success ? (res.data || []).map(normalize) : [];
      } catch (_) {
        return [];
      }
    },

    /** Një kandidat i vetëm, me historinë dhe dokumentet — një kërkesë, jo e tërë lista. */
    async get(id) {
      try {
        const res = await authFetch(`/api/applications/${id}`).then((r) => r.json());
        return res?.success ? normalize(res.data) : null;
      } catch (_) {
        return null;
      }
    },

    async save(app) {
      try {
        const now = new Date().toISOString();
        if (!app.id) {
          const payload = { ...app, createdAt: now, updatedAt: now };
          const res = await apiService().post('applications', payload);
          return normalize(res?.data || res || payload);
        } else {
          const payload = { ...app, updatedAt: now };
          const res = await apiService().put('applications', payload);
          return normalize(res?.data || res || payload);
        }
      } catch (_) {
        return app;
      }
    },

    /**
     * Një veprim mbi shumë kandidatë njëherësh.
     *
     * Serveri e kalon çdo rresht nga i njëjti kontroll si një i vetëm, ndaj
     * përgjigjja mund të jetë sukses i PJESSHËM: `{selected, succeeded, failed,
     * failures}`. Faqja duhet t'i tregojë dështimet, sepse «u krye» mbi 43 nga 50
     * është gënjeshtër.
     *
     * Gabimi i tërë kërkesës (kod arsyeje i pavlefshëm, mbi 200 rreshta) kthehet
     * si `{ error }`, i ndarë nga dështimet për rresht.
     */
    async bulkAction({ action, ids, data } = {}) {
      try {
        const res = await authFetch('/api/applications/bulk-action', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, ids, data: data || {} }),
        }).then((r) => r.json());
        if (!res?.success) return { error: res?.error || 'Veprimi dështoi.' };
        return res.data;
      } catch (_) {
        return { error: 'Lidhja me serverin dështoi.' };
      }
    },

    /** Llogaritë që mund të jenë pronare të një aplikimi (lejet efektive, jo roli). */
    async recruiters() {
      try {
        const res = await authFetch('/api/recruitment/recruiters').then((r) => r.json());
        return res?.success ? res.data : [];
      } catch (_) {
        return [];
      }
    },

    async remove(id) {
      try {
        await apiService().delete('applications', id);
        return true;
      } catch (_) {
        return false;
      }
    },
  };
}
